import { useState, useRef, MouseEvent, FC, Dispatch, useEffect } from "react";
import styled from "styled-components";

import SearchBar from "@/components/SearchBar";
import Text from "@/components/Text";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { Tags } from "@/pages/AssignTopics/components/AttachmentsField";
import Tag from "@/components/Tag";
import { IgProfile } from "@/types/resources";
import { api } from "@/utils/api";
import useDebounce from "@/hooks/useDebounce";
import { Inner, Box, Title, Item } from "@/components/Dropdown";
import FilterButton from "@/components/FilterDropdown/FilterButton";

const StyledBox = styled(Box)`
  width: 320px;
`;

const StyledTitle = styled(Title)`
  padding: 0 16px;
  margin-bottom: 20px;
`;

const StyledText = styled(Text)`
  transition: color ease-out 0.2s;
  &:hover,
  &:active {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const Header = styled.div`
  padding: 0 16px;
`;

const TitleText = styled(Text)`
  margin: 0 14px;
  margin-top: 8px;
`;

export const NoTopics = styled.div`
  text-align: center;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const TopicsHolder = styled.div`
  position: relative;
`;

const StyledInner = styled(Inner)`
  padding: 0 4px 8px;
`;

const StyledTags = styled(Tags)`
  margin-bottom: 8px;
`;

const StyledSearchBar = styled(SearchBar)`
  margin-bottom: 8px;
`;

const StyledItem = styled(Item)`
  text-align: left;
  display: block;
  padding: 5px 14px;
  ${Text} {
    transition: color ease-out 0.2s;
    &:first-child {
      margin-bottom: 2px;
      span {
        color: ${({ theme }) => theme.colors.purple};
      }
    }
  }
  &:hover {
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
`;

interface Props {
  activeProfiles: IgProfile[];
  setActiveProfiles: Dispatch<React.SetStateAction<IgProfile[]>>;
}

const IgProfilesFilter: FC<Props> = ({ setActiveProfiles, activeProfiles }) => {
  const [showFilter, setShowFilter] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [suggestions, setSuggestions] = useState<IgProfile[]>([]);

  const debouncedSearch = useDebounce(search, 400);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowFilter(false));

  const splitMatchedText = (str: string) => {
    const regExp = new RegExp(debouncedSearch, "gi");
    return str.replace(regExp, (match) => `<span>${match}</span>`);
  };

  useEffect(() => {
    if (debouncedSearch.length < 2) {
      setIsLoading(false);
      return setSuggestions([]);
    }

    setIsLoading(true);

    api
      .get<never, IgProfile[]>("/v1/ig-profiles-tracker/profiles", {
        params: {
          "filter[search]": debouncedSearch,
        },
      })
      .then((suggestions) => {
        setSuggestions(suggestions);
        setIsLoading(false);
      });
  }, [debouncedSearch]);

  const onClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setActiveProfiles([]);
    setSearch("");
    setSuggestions([]);
    setShowFilter(false);
  };

  const onProfileRemove = (
    e: MouseEvent<HTMLButtonElement>,
    profileToRemove: IgProfile
  ) => {
    e.stopPropagation();
    setActiveProfiles((prevProfiles) =>
      prevProfiles.filter((profile) => profile.id !== profileToRemove.id)
    );
  };

  const onProfileAdd = (
    e: MouseEvent<HTMLButtonElement>,
    profile: IgProfile
  ) => {
    e.stopPropagation();
    setActiveProfiles((prevProfiles) => [...prevProfiles, profile]);
    setSearch("");
  };

  const noSuggestionsFound =
    debouncedSearch.length >= 2 && !!!suggestions.length && !isLoading;

  const filteredSuggestions = suggestions.filter(
    (suggestion) => !activeProfiles.map((t) => t.id).includes(suggestion.id)
  );

  return (
    <TopicsHolder ref={ref}>
      <FilterButton
        text="Profiles"
        isActive={showFilter}
        onClick={(e) => {
          e?.stopPropagation();
          setShowFilter((prev) => !prev);
        }}
        filterApplied={!!activeProfiles.length}
      />
      {showFilter && (
        <StyledBox>
          <StyledTitle $color="black" $type="smallBold">
            Filter by username
            <StyledText
              as="button"
              $color="gray"
              $type="extraSmall"
              onClick={onClear}
            >
              Clear
            </StyledText>
          </StyledTitle>
          <Header>
            <StyledSearchBar
              value={search}
              setValue={setSearch}
              placeholder="Search by username"
            />
            {!!activeProfiles.length && (
              <StyledTags>
                {activeProfiles.map((profile) => {
                  return (
                    <Tag
                      key={profile.id}
                      onDelete={(e) => onProfileRemove(e, profile)}
                    >
                      {profile.username}
                    </Tag>
                  );
                })}
              </StyledTags>
            )}
            {noSuggestionsFound && (
              <NoTopics>
                <Text $color="black" $type="smallBold">
                  No profiles found
                </Text>
              </NoTopics>
            )}
          </Header>
          <StyledInner>
            {!!filteredSuggestions.length && (
              <>
                <TitleText $type="extraSmall">Profile suggestions</TitleText>
                {filteredSuggestions.map((suggestion) => (
                  <StyledItem
                    key={suggestion.id}
                    as="button"
                    onClick={(e: MouseEvent<HTMLButtonElement>) =>
                      onProfileAdd(e, suggestion)
                    }
                  >
                    <Text
                      dangerouslySetInnerHTML={{
                        __html: splitMatchedText(suggestion.username),
                      }}
                      $color="gray"
                      $type="smallBold"
                    />
                  </StyledItem>
                ))}
              </>
            )}
          </StyledInner>
        </StyledBox>
      )}
    </TopicsHolder>
  );
};

export default IgProfilesFilter;
